import { Vector3 } from '@babylonjs/core/Maths/math';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';

import { MirrorFeature } from '@kb3d/models/classes/features/mirror-feature';
import { SliceFeature } from '@kb3d/models/classes/features/slice-feature';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass } from '@kb3d/models/interfaces';
import { rotationToVector } from '@kb3d/view/helpers/math-helper';
import { createMirrorVisualization } from '@kb3d/view/helpers/mirror-helper';
import { FeatureRenderer } from './feature';
import { SliceGeometryProcessor } from './geometry-processor/slice-geometry';

export class SliceFeatureRenderer extends FeatureRenderer<SliceFeature> {
    modelToken = Token.SliceFeature;
    previewLayer: UtilityLayerRenderer = this.viewer.utilityLayer;
    babylonMirrorPreviewsMap = new Map<string, Mesh>();
    processor = new SliceGeometryProcessor(this.scene);

    protected updatePreviewMesh(feature: SliceFeature) {
        const scene = this.previewLayer.utilityLayerScene;
        let visualizationPlane = this.babylonMirrorPreviewsMap.get(feature.id);

        if (!visualizationPlane) {
            visualizationPlane = createMirrorVisualization(feature, scene);
            visualizationPlane.isPickable = false;
            this.babylonMirrorPreviewsMap.set(feature.id, visualizationPlane);
        }

        const currentNormal = new Vector3();
        currentNormal.set(0, 0, 1);
        const newNormal = new Vector3();
        newNormal.set(feature.normal.x, feature.normal.y, feature.normal.z);
        visualizationPlane.rotationQuaternion = rotationToVector(newNormal, currentNormal);
        let parentBoundingBox = undefined;
        if (feature._parent) {
            parentBoundingBox = this.viewer.getMeshBoundingBox(feature._parent);
        }
        if (parentBoundingBox) {
            const scale = parentBoundingBox.minWorld.subtract(parentBoundingBox.maxWorld).length();
            visualizationPlane.scaling.set(scale, scale, scale);
        }

        if (feature._parent) {
            const featureParent = this.viewer.getRendererNode(feature._parent);
            const meshPosition = featureParent.getAbsolutePosition();
            meshPosition.addInPlace(feature.intersect.toVec3());
            visualizationPlane.position.set(meshPosition.x, meshPosition.y, meshPosition.z);
        }
    }

    update3dInternal(node: ITrackedClass<SliceFeature>) {
        const visualizationPlane = this.babylonMirrorPreviewsMap.get(node.id);

        if (node._previewVisible) {
            this.updatePreviewMesh(node);
            if (node.enabled) {
                visualizationPlane?.setEnabled(true);
            }
        } else {
            visualizationPlane?.setEnabled(false);
        }
    }

    delete3d(node: ITrackedClass<SliceFeature> | ITrackedClass<MirrorFeature>) {
        if (this.babylonMirrorPreviewsMap) {
            const featureInstance = this.babylonMirrorPreviewsMap.get(node.id);

            if (featureInstance) {
                featureInstance.dispose(false, true);
            }
            this.babylonMirrorPreviewsMap.delete(node.id);
        }
        super.delete3d(node as ITrackedClass<SliceFeature>);
    }
}
